/**
 * Aviso central sobre o mapa: carregando a primeira imagem, erro, sem
 * conexão, ou nenhum quadro no período escolhido.
 */
import { CalendarX2, LoaderCircle, SatelliteDish, WifiOff, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { getProductName } from '../config/products';
import { useCatalogStore } from '../goes/catalog';
import { checkNow } from '../loop/autoRefresh';
import { usePlanStore } from '../loop/plan';
import { useLoopStore } from '../loop/store';
import { useGoesStore } from '../state/goes';
import { useUiStore } from '../state/ui';
import { useBaseProductId } from './hooks';
import { Button } from './primitives';

function Card(props: { icon: LucideIcon; spin?: boolean; warn?: boolean; children: ReactNode; action?: ReactNode }) {
  const Icon = props.icon;
  return (
    <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center p-6">
      <div
        className="pointer-events-auto flex max-w-sm border border-ink-600/80 bg-ink-900/95 flex-col items-center gap-3 rounded-2xl px-6 py-5 text-center shadow-2xl"
        role="status"
      >
        <Icon
          className={props.spin ? 'size-7 animate-spin text-mata-400' : props.warn ? 'size-7 text-yellow-300' : 'size-7 text-ink-300'}
          aria-hidden
        />
        <p className="text-sm text-ink-100">{props.children}</p>
        {props.action}
      </div>
    </div>
  );
}

export function LoadingOverlay() {
  const shown = usePlanStore((s) => s.firstFrameShown);
  const empty = usePlanStore((s) => s.plan !== null && s.plan.frames.length === 0);
  const base = useBaseProductId();
  const status = useCatalogStore((s) => (base ? s.entries[base]?.status : undefined));
  const slow = useCatalogStore((s) => (base ? !!s.entries[base]?.slow : false));
  const failedNow = usePlanStore((s) => Object.values(s.frameStatus).includes('failed'));
  const custom = useLoopStore((s) => s.period === 'custom');
  const online = useUiStore((s) => s.online);
  const anyVisible = useGoesStore((s) => s.layers.some((l) => l.visible));

  if (empty) {
    return (
      <Card
        icon={CalendarX2}
        warn
        action={
          <Button size="sm" variant="primary" onClick={() => useLoopStore.getState().setSettings({ period: '3h' })}>
            Ver as imagens mais recentes
          </Button>
        }
      >
        Não há imagens do satélite no período escolhido. Escolha outro período ou volte às mais recentes.
      </Card>
    );
  }
  if (shown || !anyVisible) return null;

  if (!online) {
    return (
      <Card icon={WifiOff} warn>
        Sem conexão com a internet. As imagens aparecem assim que a conexão voltar.
      </Card>
    );
  }
  if (status === 'error') {
    return (
      <Card
        icon={SatelliteDish}
        warn
        action={
          <Button size="sm" onClick={() => void checkNow()}>
            Tentar agora
          </Button>
        }
      >
        Não foi possível falar com o servidor da NOAA agora. Tentando de novo automaticamente…
      </Card>
    );
  }
  if (failedNow) {
    return (
      <Card icon={LoaderCircle} spin>
        A NOAA não entregou a imagem deste horário. Buscando o quadro anterior…
      </Card>
    );
  }
  const what = `${custom ? 'as imagens' : 'as imagens mais recentes'} do GOES-19${base ? ` — ${getProductName(base)}` : ''}`;
  return (
    <Card icon={LoaderCircle} spin>
      {slow ? `A NOAA está demorando mais que o normal para responder. Ainda buscando ${what}…` : `Buscando ${what}…`}
    </Card>
  );
}
